import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { FiCheck, FiX, FiUser, FiMail, FiClock, FiArrowRight } from 'react-icons/fi';
import './AcceptInvite.css';

const AcceptInvite = () => {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        console.log('[AcceptInvite] Fetching invitation:', inviteCode);

        const invitationsRef = collection(db, 'clientInvitations');
        const inviteQuery = query(
          invitationsRef,
          where('inviteCode', '==', inviteCode),
          where('status', '==', 'pending')
        );

        const querySnapshot = await getDocs(inviteQuery);

        if (querySnapshot.empty) {
          setError('Invitation not found or has expired');
          setLoading(false);
          return;
        }

        const inviteDoc = querySnapshot.docs[0];
        const inviteData = inviteDoc.data();

        // Check if invitation has expired
        const expiresAt = inviteData.expiresAt?.toDate();
        if (expiresAt && expiresAt < new Date()) {
          setError('This invitation has expired');
          setLoading(false);
          return;
        }

        setInvitation({
          id: inviteDoc.id,
          ...inviteData
        });

        console.log('[AcceptInvite] Invitation found:', inviteData);
      } catch (err) {
        console.error('[AcceptInvite] Error fetching invitation:', err);
        setError('Error loading invitation. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (inviteCode) {
      fetchInvitation();
    }
  }, [inviteCode]);

  // Handle invitation acceptance
  const handleAcceptInvitation = async () => {
    if (!currentUser || !userProfile) {
      // Redirect to signup with invite code
      navigate(`/signup?invite=${inviteCode}`);
      return;
    }

    if (userProfile.role !== 'client') {
      setError('Only client accounts can accept trainer invitations');
      return;
    }

    setAccepting(true);

    try {
      // Update user's trainer ID
      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        trainerId: invitation.trainerId,
        updatedAt: new Date()
      });

      // Update invitation status
      const inviteDocRef = doc(db, 'clientInvitations', invitation.id);
      await updateDoc(inviteDocRef, {
        status: 'accepted',
        acceptedAt: new Date(),
        acceptedBy: currentUser.uid
      });

      console.log('[AcceptInvite] Invitation accepted successfully');

      // Show success message and redirect
      alert(`Success! You're now connected to ${invitation.trainerName}`);
      navigate('/unique-physique/client-dashboard');
    } catch (err) {
      console.error('[AcceptInvite] Error accepting invitation:', err);
      setError('Error accepting invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="accept-invite-page">
        <div className="invite-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="accept-invite-page">
        <div className="invite-container error">
          <div className="error-icon">
            <FiX />
          </div>
          <h2>Invitation Error</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // No invitation found
  if (!invitation) {
    return (
      <div className="accept-invite-page">
        <div className="invite-container error">
          <div className="error-icon">
            <FiX />
          </div>
          <h2>Invitation Not Found</h2>
          <p>This invitation link is invalid or has been used.</p>
          <button className="btn-primary" onClick={() => navigate('/login')}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Invitation details
  return (
    <div className="accept-invite-page">
      <div className="invite-container">
        <div className="invite-header">
          <div className="success-icon">
            <FiCheck />
          </div>
          <h2>You've Been Invited!</h2>
          <p className="invite-subtitle">
            Join {invitation.trainerName}'s coaching program
          </p>
        </div>

        <div className="invite-details">
          <div className="detail-item">
            <div className="detail-icon">
              <FiUser />
            </div>
            <div className="detail-content">
              <label>Coach</label>
              <span>{invitation.trainerName}</span>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <FiMail />
            </div>
            <div className="detail-content">
              <label>Coach Email</label>
              <span>{invitation.trainerEmail}</span>
            </div>
          </div>

          <div className="detail-item">
            <div className="detail-icon">
              <FiClock />
            </div>
            <div className="detail-content">
              <label>Expires</label>
              <span>{invitation.expiresAt?.toDate().toLocaleDateString()}</span>
            </div>
          </div>

          {invitation.message && (
            <div className="invite-message">
              <p>"{invitation.message}"</p>
            </div>
          )}
        </div>

        <div className="invite-actions">
          {currentUser && userProfile ? (
            <>
              <p className="logged-in-message">
                Logged in as <strong>{userProfile.displayName}</strong>
              </p>
              <button
                className="btn-accept"
                onClick={handleAcceptInvitation}
                disabled={accepting}
              >
                {accepting ? 'Accepting...' : 'Accept Invitation'}
                <FiArrowRight />
              </button>
              <button className="btn-cancel" onClick={() => navigate('/unique-physique/client-dashboard')}>
                Decline
              </button>
            </>
          ) : (
            <>
              <p className="signup-message">
                Create an account or sign in to accept this invitation
              </p>
              <button
                className="btn-accept"
                onClick={() => navigate(`/signup?invite=${inviteCode}`)}
              >
                Create Account
                <FiArrowRight />
              </button>
              <button
                className="btn-secondary"
                onClick={() => navigate(`/login?invite=${inviteCode}`)}
              >
                Already have an account? Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
